import { Link } from 'react-router-dom';
import type { LeaderboardRow } from '../../data/types';
import { formatCI, formatN, formatRank, formatRankChange, formatScore } from '../../lib/format';
import styles from './Leaderboard.module.css';

interface LeaderboardProps {
  rows: LeaderboardRow[];
  caption: string;
  variant?: 'primary' | 'subordinate';
}

export function Leaderboard({ rows, caption, variant = 'primary' }: LeaderboardProps) {
  // Compute the visual width of every CI bar relative to the widest CI on the
  // table — the bar gives the eye a printed reading of where the score sits
  // inside its uncertainty range without making the table look like a chart.
  const allLow = rows.map((r) => r.vibe_score_ci[0]);
  const allHigh = rows.map((r) => r.vibe_score_ci[1]);
  const minScore = Math.min(...allLow);
  const maxScore = Math.max(...allHigh);
  const span = Math.max(1, maxScore - minScore);

  return (
    <table className={`${styles.table} ${variant === 'subordinate' ? styles.subordinate : ''}`}>
      <caption className={styles.caption}>{caption}</caption>
      <thead>
        <tr>
          <th scope="col" className={styles.thRank}>Rank</th>
          <th scope="col" className={styles.thModel}>Model</th>
          <th scope="col" className={styles.thScore}>Vibe</th>
          <th scope="col" className={styles.thRange}>95% interval</th>
          <th scope="col" className={styles.thN}>Sample</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const change = formatRankChange(row.rank_change);
          const lowPct = ((row.vibe_score_ci[0] - minScore) / span) * 100;
          const highPct = ((row.vibe_score_ci[1] - minScore) / span) * 100;
          const scorePct = ((row.vibe_score - minScore) / span) * 100;
          return (
            <tr key={row.id}>
              <td className={styles.tdRank}>
                <div className={styles.rankInner}>
                  <span className={styles.rankNumber}>{formatRank(row.rank)}</span>
                  <span
                    className={`${styles.rankChange} ${styles[`rankChange_${change.tone}`]}`}
                    aria-label={change.label}
                    title={change.label}
                  >
                    {change.glyph}
                  </span>
                </div>
              </td>
              <td className={styles.tdModel}>
                <div className={styles.modelInner}>
                  <Link to={`/models/${row.id}`} className={styles.modelLink}>
                    <span className={styles.modelName}>{row.display_name}</span>
                  </Link>
                  <span className={styles.modelMeta}>
                    <span className={styles.modelProvider}>{row.provider}</span>
                    <span className={styles.modelDot}>·</span>
                    <span className={styles.modelSlug}>{row.openrouter_id}</span>
                  </span>
                </div>
              </td>
              <td className={styles.tdScore}>
                <span className={styles.score}>{formatScore(row.vibe_score)}</span>
              </td>
              <td className={styles.tdRange}>
                <div className={styles.rangeInner}>
                  <div
                    className={styles.ciBar}
                    style={{
                      ['--ci-low' as string]: `${lowPct}%`,
                      ['--ci-high' as string]: `${highPct}%`,
                      ['--ci-mark' as string]: `${scorePct}%`,
                    }}
                    aria-hidden="true"
                  >
                    <div className={styles.ciTrack} />
                    <div className={styles.ciRange} />
                    <div className={styles.ciMark} />
                  </div>
                  <span className={styles.ciText}>{formatCI(row.vibe_score_ci)}</span>
                </div>
              </td>
              <td className={styles.tdN}>{formatN(row.sample_size)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
